import { useCallback, useEffect, useMemo, useState } from "react";
import { discoverCommunitiesToggleBtnData } from "./components/data/discoverCommunitiesData.ts";
import { ConstellationView } from "./components/constellationView/ConstellationView.tsx";
import { TraditionalView } from "./components/traditionalView/TraditionalView.tsx";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks.ts";
import { fetchPersonasData } from "../../stores/thunks/personasThunk.tsx";
import { useLocation, useNavigate } from "react-router";
import { getParamsName } from "../../helpers/paramsName/paramsName.ts";
import { FilterParamsType } from "./discoverCommunitiesTypes.ts";
import { InterestType } from "../../stores/types/personasTypes.tsx";
import personaEmpty from "/images/empty_states/persona-empty.svg";
import { faStars } from "@fortawesome/pro-solid-svg-icons";
import { CreateBriefDialog } from "../creatorBriefs/components/createBriefDialog/CreateBriefDialog.tsx";
import { fetchCreatorBriefsThunk } from "../../stores/thunks/creatorBriefsThunk.tsx";
import {
  fetchPersonaCommunitiesAggregateData,
  fetchPersonaCommunitiesData,
} from "../../stores/thunks/discoverCommunitiesThunk.tsx";
import { setSelectedPersona } from "../../stores/slice/discoverCommunitiesSlice.tsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  RevActionsHeader,
  RevButton,
  RevEmptyPage,
  RevLink,
  RevSpinner,
  RevToggleButtonGroup,
} from "@revmatics/catalyst";

export const DiscoverCommunitiesPage = () => {
  const [activeTab, setActiveTab] = useState("traditional_view");
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    personaCreate: ffPersonaCreate,
    creatorBriefGenerate: ffCreatorBriefGenerate,
    landingPageGenerate: ffLandingPageGenerate,
    toggleDiscoverCommunitiesView: ffToggleDiscoverCommunitiesView,
    isTutorialDiscoverCommunities: ffTutorialDiscoverCommunities,
    showCroLinks: ffShowCroLinks,
  } = useFlags();
  const personas = useAppSelector((state) => state.personas.personas);
  const isLoading = useAppSelector((state) => state.personas.isLoadingList);
  const communities = useAppSelector(
    (state) => state.discoverCommunities.communities,
  );
  const selectedPersona = useAppSelector(
    (state) => state.discoverCommunities.selectedPersona,
  );
  const [selectedInterests, setSelectedInterests] = useState<Set<string>>(
    new Set(),
  );
  const [selectedInterestsCommunity, setSelectedInterestsCommunity] = useState<
    Set<string>
  >(new Set());
  const [selectedCommunity, setSelectedCommunity] = useState<Set<number>>(
    new Set(),
  );
  const [selectedAffiliation, setSelectedAffiliation] = useState<Set<string>>(
    new Set(),
  );
  const [selectedParentInterest, setSelectedParentInterest] =
    useState<InterestType | null>(null);

  const [filter, setFilter] = useState<FilterParamsType>({
    name: "",
    sort: "size",
    min_size: "",
    max_size: "",
  });
  const [isFirstLoading, setFirstLoading] = useState(true);
  const [isCreateBriefDialogOpen, setIsCreateBriefDialogOpen] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    let selectedPersonaItem = null;
    if (personas && personas.length > 0) {
      if (searchParams.has(getParamsName.persona)) {
        selectedPersonaItem =
          personas.find(
            (persona) =>
              persona.id.toString() === searchParams.get(getParamsName.persona),
          ) || personas[0];
      } else {
        selectedPersonaItem = personas.find(
          (item) => item.interests.length > 0,
        );
      }
    }
    dispatch(setSelectedPersona(selectedPersonaItem || null));
  }, [dispatch, location.search, personas]);

  const paramsData = useMemo(() => {
    const params = new URLSearchParams();
    if (
      !selectedParentInterest &&
      selectedAffiliation.size === 0 &&
      selectedInterests.size === 0 &&
      !filter.name &&
      !filter.max_size &&
      !filter.min_size
    ) {
      return undefined;
    }
    Object.keys(filter).forEach((item) => {
      const key = item as keyof typeof filter;
      if (filter[key]) {
        const value =
          key === "max_size" || key === "min_size"
            ? Number(filter[key])
            : filter[key];
        params.append(key, String(value));
      }
    });
    if (selectedAffiliation.size > 0) {
      [...selectedAffiliation].forEach((item) =>
        params.append("affiliation", item),
      );
    }
    if (selectedInterests.size > 0) {
      [...selectedInterests].forEach((item) => params.append("interest", item));
    }
    if (selectedParentInterest) {
      params.append("interest_group_id", selectedParentInterest.id.toString());
    }
    return params;
  }, [selectedParentInterest, selectedAffiliation, selectedInterests, filter]);

  useEffect(() => {
    if (selectedPersona) {
      setFirstLoading(true);
    }
  }, [selectedPersona]);

  useEffect(() => {
    if (selectedPersona) {
      dispatch(
        fetchPersonaCommunitiesData({
          id: selectedPersona.id.toString(),
          params: paramsData,
        }),
      ).finally(() => {
        if (isFirstLoading) {
          dispatch(
            fetchPersonaCommunitiesAggregateData({
              id: selectedPersona.id.toString(),
            }),
          );
        }
        setFirstLoading(false);
      });
    }
  }, [dispatch, isFirstLoading, paramsData, selectedPersona]);

  const handleCreationSuccess = useCallback(() => {
    dispatch(fetchCreatorBriefsThunk()).then(() => navigate("/creator-briefs"));
  }, [dispatch, navigate]);

  useEffect(() => {
    if (
      (selectedCommunity.size > 0 ||
        (selectedCommunity.size === 0 &&
          selectedInterestsCommunity.size > 0)) &&
      communities &&
      selectedCommunity.size < communities.length
    ) {
      const newSelectedInterestsCommunity: Set<string> = new Set();
      if (selectedCommunity.size > 0) {
        communities.map((community) => {
          if (selectedCommunity.has(community.community.id)) {
            community.community.interests.map((interest: string) =>
              selectedInterests.size > 0
                ? selectedInterests.has(interest) &&
                  newSelectedInterestsCommunity.add(interest)
                : newSelectedInterestsCommunity.add(interest),
            );
          }
        });
      }
      if (
        !(
          newSelectedInterestsCommunity.size ===
            selectedInterestsCommunity.size &&
          [...newSelectedInterestsCommunity].every((item) =>
            selectedInterestsCommunity.has(item),
          )
        )
      ) {
        setSelectedInterestsCommunity(newSelectedInterestsCommunity);
      }
    }
  }, [
    selectedCommunity,
    communities,
    selectedInterests,
    selectedInterestsCommunity,
  ]);

  useEffect(() => {
    if (communities && communities.length > 0) {
      setSelectedCommunity(
        new Set(communities.map((item) => item.community.id)),
      );
    }
  }, [communities]);

  useEffect(() => {
    if (personas === null) {
      dispatch(fetchPersonasData());
    }
  }, [dispatch, personas]);

  if (isLoading) {
    return (
      <div className="h-full" data-testid="spinner-discover-communities">
        <RevSpinner size="large" centered color="primary" />
      </div>
    );
  }

  if (!isLoading && personas && personas.length === 0 && !selectedPersona) {
    return (
      <RevEmptyPage
        image={personaEmpty}
        title="No Personas"
        text="Build your first persona, and unlock the persona universe!"
        to={ffPersonaCreate ? "?personaCreate=true" : "/personas"}
        textBtn={ffPersonaCreate ? "Create Persona" : "Go back"}
        data-testid="discover-communities-empty"
      />
    );
  }

  return (
    <div className="flex flex-col">
      <RevActionsHeader
        title="Discover Communities"
        videoBtn={ffTutorialDiscoverCommunities}
      />
      <div className="mb-2">
        {ffToggleDiscoverCommunitiesView && (
          <RevToggleButtonGroup
            data={discoverCommunitiesToggleBtnData}
            idPage="discover-communities"
            selected={activeTab}
            handleSelect={setActiveTab}
          />
        )}
        {activeTab === "traditional_view" && (
          <TraditionalView
            isFirstLoading={isFirstLoading}
            selectedInterests={selectedInterests}
            setSelectedInterests={setSelectedInterests}
            setSelectedCommunity={setSelectedCommunity}
            selectedCommunity={selectedCommunity}
            filter={filter}
            setFilter={setFilter}
            selectedAffiliation={selectedAffiliation}
            setSelectedAffiliation={setSelectedAffiliation}
            selectedParentInterest={selectedParentInterest}
            setSelectedParentInterest={setSelectedParentInterest}
            selectedInterestsCommunity={selectedInterestsCommunity}
            setSelectedInterestsCommunity={setSelectedInterestsCommunity}
          />
        )}
        {activeTab === "constellation_view" && <ConstellationView />}
      </div>
      {(ffCreatorBriefGenerate || ffLandingPageGenerate) && (
        <div className="sticky -bottom-4 left-0 -translate-x-4 lg:-translate-x-6 w-[calc(100%_+_32px)] lg:w-[calc(100%_+_48px)] bg-primary-25 px-4 py-2 flex flex-col md:flex-row gap-4 justify-center">
          {ffCreatorBriefGenerate && (
            <RevButton
              text="Generate Creator Brief"
              iconLeft={faStars}
              buttonSize="medium"
              handleClick={() => setIsCreateBriefDialogOpen(true)}
            />
          )}
          {ffLandingPageGenerate && ffShowCroLinks && (
            <RevLink
              iconLeft={faStars}
              text="Generate Landing Page "
              pathname={`${import.meta.env.VITE_CRO_API_URL}/funnels`}
              local={false}
              displayAs="button"
              buttonSize="medium"
              target="_self"
            />
          )}
        </div>
      )}
      <CreateBriefDialog
        isOpen={isCreateBriefDialogOpen}
        setIsOpen={setIsCreateBriefDialogOpen}
        onSuccess={handleCreationSuccess}
      />
    </div>
  );
};
